/**
 * ServiceTable — Ongoing and Completed service requests.
 *
 * Responsive strategy:
 *   < md  → card list (no horizontal scroll, full info)
 *   ≥ md  → data table with progress bar
 *
 * Interaction model:
 *   - The service name is a real router `Link`, so keyboard focus, middle-click
 *     and open-in-new-tab all behave normally. On cards that link is stretched
 *     over the whole card; on the desktop table the row also navigates on click
 *     as a mouse convenience, while the table keeps its native row semantics.
 *   - A "⋯" menu holds the secondary actions (view, request closure). The
 *     closure dialog is owned by ServiceTable and rendered outside the rows —
 *     a dialog nested inside a clickable row would bubble its portalled React
 *     events back up to that row.
 */
import * as React from 'react';
import { formatCurrency, formatRelative } from '@finmin/utils';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
} from '@finmin/ui';
import { ArrowUpRight, CircleSlash, Clock, MoreHorizontal, PiggyBank } from 'lucide-react';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import { RequestClosureDialog } from '@/features/service-request/components/request-closure-dialog';
import { getServiceMeta, getRequestStatusMeta, isCompleted } from '../constants';

/** Green "Saved ₹X" chip — only rendered when the admin has recorded a value. */
function SavedChip({ request: r }: { request: CustomerServiceRequestResponse }) {
  if (!isCompleted(r) || r.money_saved == null) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
      style={{ backgroundColor: 'hsl(142 71% 45% / 0.12)', color: 'hsl(142 71% 30%)' }}
    >
      <PiggyBank className="h-3 w-3" aria-hidden />
      Saved {formatCurrency(r.money_saved)}
    </span>
  );
}

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

// ── Variant colour map ────────────────────────────────────────────────────────

type VariantStyle = { text: string; bg: string; dot: string };

const MUTED_STYLE: VariantStyle = {
  text: 'hsl(var(--muted-foreground))',
  bg:   'hsl(var(--muted))',
  dot:  'hsl(var(--muted-foreground))',
};

const VARIANT_STYLES: Record<string, VariantStyle> = {
  amber: {
    text: 'hsl(33 100% 40%)',
    bg:   'hsl(33 100% 50% / 0.10)',
    dot:  'hsl(33 100% 50%)',
  },
  blue: {
    text: 'hsl(217 91% 48%)',
    bg:   'hsl(217 91% 60% / 0.10)',
    dot:  'hsl(217 91% 60%)',
  },
  green: {
    text: 'hsl(142 71% 32%)',
    bg:   'hsl(142 71% 45% / 0.10)',
    dot:  'hsl(142 71% 45%)',
  },
  muted: MUTED_STYLE,
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  requests: CustomerServiceRequestResponse[];
  isLoading?: boolean;
  emptyMessage: string;
}

/** Opens the closure dialog for one request. Owned by ServiceTable. */
type RequestClosure = (request: CustomerServiceRequestResponse) => void;

// ── Component ─────────────────────────────────────────────────────────────────

export function ServiceTable({ requests, isLoading, emptyMessage }: Props) {
  const [closureFor, setClosureFor] = React.useState<CustomerServiceRequestResponse | null>(null);

  if (isLoading) return <ServiceListSkeleton />;

  if (requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed border-border text-center">
        <p className="text-sm font-medium text-foreground">{emptyMessage}</p>
        <p className="text-xs text-muted-foreground mt-1">
          Services you start will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile card list (hidden on md+) */}
      <div className="md:hidden space-y-3">
        {requests.map((r, idx) => (
          <ServiceMobileCard key={r.id} request={r} index={idx} onRequestClosure={setClosureFor} />
        ))}
      </div>

      {/* Desktop table (hidden below md) */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border border-border animate-fade-in">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-muted/40 border-b border-border">
              {['Service', 'Enrolled', 'ETA', 'Stage', 'Progress', ''].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap"
                  style={h === '' ? { textAlign: 'right' } : undefined}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {requests.map((r, idx) => (
              <ServiceTableRow key={r.id} request={r} index={idx} onRequestClosure={setClosureFor} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Rendered outside the rows on purpose — see the file header. */}
      {closureFor && (
        <RequestClosureDialog
          serviceRequestId={closureFor.id}
          serviceTitle={getServiceMeta(closureFor.service_type).title}
          open
          onOpenChange={(next) => {
            if (!next) setClosureFor(null);
          }}
        />
      )}
    </>
  );
}

// ── Row actions ("⋯" menu) ───────────────────────────────────────────────────

function RowActionsMenu({
  request: r,
  serviceTitle,
  onRequestClosure,
}: {
  request: CustomerServiceRequestResponse;
  serviceTitle: string;
  onRequestClosure: RequestClosure;
}) {
  const navigate = useNavigate();
  const closure = r.service_closure;
  const canRequestClosure = closure?.can_request_closure ?? false;
  const closurePending = closure?.status === 'pending_admin_action';

  return (
    // modal={false} is required, not cosmetic. Both a modal menu and a modal
    // dialog set `pointer-events: none` on <body>, and opening the dialog from
    // this menu's onSelect loses one of the two restores, leaving the whole page
    // inert after the dialog closes. Verified by removing it: the page froze.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`More actions for ${serviceTitle}`}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[13rem]">
        <DropdownMenuItem
          onSelect={() => void navigate({ to: '/service-request/$id', params: { id: r.id } })}
        >
          <ArrowUpRight aria-hidden />
          View service
        </DropdownMenuItem>
        {(canRequestClosure || closurePending) && <DropdownMenuSeparator />}
        {canRequestClosure && (
          <DropdownMenuItem
            onSelect={() => onRequestClosure(r)}
            className="text-destructive focus:text-destructive"
          >
            <CircleSlash aria-hidden />
            Request service closure
          </DropdownMenuItem>
        )}
        {closurePending && (
          <DropdownMenuItem disabled>
            <Clock aria-hidden />
            Closure under review
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Desktop table row ─────────────────────────────────────────────────────────

function ServiceTableRow({
  request: r,
  index = 0,
  onRequestClosure,
}: {
  request: CustomerServiceRequestResponse;
  index?: number;
  onRequestClosure: RequestClosure;
}) {
  const meta     = getServiceMeta(r.service_type);
  const Icon     = meta.Icon;
  const workflow = getRequestStatusMeta(r);
  const vstyle   = VARIANT_STYLES[workflow.variant] ?? MUTED_STYLE;
  const navigate = useNavigate();
  const staggerClass = `stagger-${Math.min((index % 8) + 1, 8)}`;

  const enrolled = r.created_at
    ? new Date(r.created_at).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      })
    : '—';

  const eta = r.expected_tat ?? (
    r.tat_min && r.tat_max ? `${r.tat_min}–${r.tat_max} days` : '—'
  );

  /**
   * Mouse convenience only. The service name is a real link, which is what
   * keyboard and assistive tech use, so the row keeps its native `row` role.
   *
   * Two kinds of click must be ignored. The actions menu renders in a portal,
   * so it is not inside this row in the DOM, yet React still bubbles its events
   * here through the component tree — `contains` is what tells the two apart.
   * And a click on the row's own link or button belongs to that control.
   */
  function handleRowClick(event: React.MouseEvent<HTMLTableRowElement>) {
    const target = event.target as HTMLElement;
    if (!event.currentTarget.contains(target)) return;
    if (target.closest('a,button')) return;
    void navigate({ to: '/service-request/$id', params: { id: r.id } });
  }

  return (
    <tr
      onClick={handleRowClick}
      className={`group cursor-pointer hover:bg-muted/30 transition-colors animate-slide-up-fade ${staggerClass}`}
    >
      {/* Service name */}
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
            style={{ backgroundColor: MINT }}
          >
            <Icon className="h-4 w-4" style={{ color: FOREST }} aria-hidden />
          </div>
          <div className="min-w-0">
            <Link
              to="/service-request/$id"
              params={{ id: r.id }}
              className="block text-sm font-semibold text-foreground truncate max-w-[160px] hover:underline focus-visible:underline focus-visible:outline-none"
            >
              {meta.title}
            </Link>
            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider mt-0.5">
              {r.id.split('-')[0]}
            </p>
          </div>
        </div>
      </td>

      {/* Enrolled */}
      <td className="px-4 py-3.5 text-sm text-muted-foreground whitespace-nowrap">{enrolled}</td>

      {/* ETA */}
      <td className="px-4 py-3.5 text-sm text-muted-foreground whitespace-nowrap">{eta}</td>

      {/* Workflow stage */}
      <td className="px-4 py-3.5">
        <div className="flex flex-col items-start gap-1.5">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ backgroundColor: vstyle.bg, color: vstyle.text }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full shrink-0"
              style={{ backgroundColor: vstyle.dot }}
              aria-hidden
            />
            {workflow.label}
          </span>
          <SavedChip request={r} />
        </div>
      </td>

      {/* Progress bar */}
      <td className="px-4 py-3.5 w-40">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold text-muted-foreground">
            {workflow.progress}%
          </span>
          <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${workflow.progress}%`, backgroundColor: vstyle.dot }}
            />
          </div>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3.5 text-right">
        <RowActionsMenu request={r} serviceTitle={meta.title} onRequestClosure={onRequestClosure} />
      </td>
    </tr>
  );
}

// ── Mobile card ───────────────────────────────────────────────────────────────

function ServiceMobileCard({
  request: r,
  index = 0,
  onRequestClosure,
}: {
  request: CustomerServiceRequestResponse;
  index?: number;
  onRequestClosure: RequestClosure;
}) {
  const meta     = getServiceMeta(r.service_type);
  const Icon     = meta.Icon;
  const workflow = getRequestStatusMeta(r);
  const vstyle   = VARIANT_STYLES[workflow.variant] ?? MUTED_STYLE;
  const staggerClass = `stagger-${Math.min((index % 8) + 1, 8)}`;

  return (
    <div
      className={`relative w-full rounded-2xl border border-border bg-background p-4 space-y-3 transition-all hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99] focus-within:ring-2 focus-within:ring-ring animate-slide-up-fade ${staggerClass}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: MINT }}
        >
          <Icon className="h-5 w-5" style={{ color: FOREST }} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          {/* Stretched link: the whole card is tappable, but it stays a real
              link, so focus, middle-click and open-in-new-tab keep working. */}
          <Link
            to="/service-request/$id"
            params={{ id: r.id }}
            className="block truncate text-sm font-semibold text-foreground before:absolute before:inset-0 before:rounded-2xl before:content-[''] focus-visible:outline-none"
          >
            {meta.title}
          </Link>
          <p className="text-xs text-muted-foreground mt-0.5">
            {r.created_at ? formatRelative(r.created_at) : '—'}
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0"
          style={{ backgroundColor: vstyle.bg, color: vstyle.text }}
        >
          {workflow.label}
        </span>
        {/* Sits above the stretched link so the menu stays tappable. */}
        <div className="relative z-10">
          <RowActionsMenu request={r} serviceTitle={meta.title} onRequestClosure={onRequestClosure} />
        </div>
      </div>

      {/* Progress */}
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">Progress</span>
          <span className="text-xs font-semibold text-muted-foreground">
            {workflow.progress}%
          </span>
        </div>
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${workflow.progress}%`, backgroundColor: vstyle.dot }}
          />
        </div>
      </div>

      {isCompleted(r) && r.money_saved != null && (
        <div className="pt-1">
          <SavedChip request={r} />
        </div>
      )}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function ServiceListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-border p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
          <Skeleton className="h-1.5 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}
